import React, { useState, useEffect } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, deleteObject } from 'firebase/storage';
import { FIREBASE_DB } from '../firebase.config';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { FileText, ChevronDown, Eye, Search } from 'lucide-react';

const W2Section = () => {
  const [w2Documents, setW2Documents] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  useEffect(() => {
    const fetchW2Documents = async () => {
      try {
        setLoading(true);
        const w2Query = query(
          collection(FIREBASE_DB, 'w2Documents'),
          where('year', '==', selectedYear)
        );
        const querySnapshot = await getDocs(w2Query);
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Safe sorting that handles undefined/null employeeName
        const sortedDocuments = documents.sort((a, b) => {
          const nameA = (a.employeeName || '').toLowerCase();
          const nameB = (b.employeeName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setW2Documents(sortedDocuments);
        setFilteredDocuments(sortedDocuments);
      } catch (error) {
        console.error('Error fetching W2 documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchW2Documents();
  }, [selectedYear]);

  // Filter documents when search term changes
  useEffect(() => {
    const filtered = w2Documents.filter(doc => 
      (doc.employeeName || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredDocuments(filtered);
  }, [searchTerm, w2Documents]);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          <h2 className="text-xl font-semibold">W2 Documents</h2>
        </div>
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm"
            />
          </div>
          {/* Year Dropdown */}
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
            >
              Year: {selectedYear}
              <ChevronDown className="w-4 h-4" />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                {years.map(year => (
                  <button
                    key={year}
                    onClick={() => {
                      setSelectedYear(year);
                      setIsDropdownOpen(false);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchTerm ? 'No matching documents found' : `No W2 documents found for ${selectedYear}`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Upload Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {doc.employeeName || 'Unknown Employee'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {doc.uploadedAt?.toDate ? 
                      new Date(doc.uploadedAt.toDate()).toLocaleDateString() :
                      'Unknown Date'
                    }
                  </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <a
                       href={doc.fileUrl}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                     >
                       <Eye className="w-4 h-4" />
                       View
                     </a>
                   </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default W2Section; 
